import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { QRCodeSVG } from 'qrcode.react';
import { useLocalStorage } from 'usehooks-ts';

import { logEvent } from '~/config/firebase.config';

import styles from './AnimatorPromo.module.css';

/**
 * Adoption funnel for "Animator Control" (piloting the session from the phone).
 * Mounted on the bar/anim game launcher (HomeAnimation), which runs on the TV.
 *
 * Two surfaces, mutually exclusive so they never stack in the same corner:
 *  - a one-shot intro spotlight card (until dismissed, persisted in localStorage)
 *  - a persistent badge that takes over the same corner once the card is closed,
 *    keeping a permanent, scannable entry point.
 *
 * The TV is client-facing and has no reliable pointer, so both surfaces always
 * expose a scannable QR (deep-linked to the establishment cockpit) rather than
 * relying on a click. Analytics measure discovery → first use.
 */

// Per-establishment key: a multi-site operator who dismisses the card on one
// establishment should still discover it on the others (same browser).
const dismissKey = (clientID: string) => `animator_promo_dismissed_${clientID}`;

function IconClose() {
    return (
        <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            aria-hidden="true"
        >
            <path d="M6 6l12 12M18 6L6 18" />
        </svg>
    );
}

function IconPhone() {
    return (
        <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
        >
            <rect x="7" y="2" width="10" height="20" rx="2.5" />
            <path d="M11 18h2" />
        </svg>
    );
}

export type AnimatorPromoProps = {
    clientID: string;
    /** Posée dans une colonne (menu TV) au lieu du coin. */
    inColumn?: boolean;
};

function AnimatorPromoSpotlight({
    clientID,
    link,
    onDismiss,
    inColumn,
}: {
    clientID: string;
    link: string;
    onDismiss: () => void;
    inColumn?: boolean;
}) {
    const { t } = useTranslation();
    // Fire the "shown" event once, only when the card actually renders.
    const shownRef = useRef(false);
    useEffect(() => {
        if (shownRef.current) return;
        shownRef.current = true;
        logEvent('animator_promo_modal_shown', clientID, { clientID });
    }, [clientID]);

    return (
        <aside className={`${styles.card} ${inColumn ? styles.inColumn : ''}`} aria-label={t('animatorPromo.cardAria')}>
            <div className={styles.cardHead}>
                <span className={styles.badgeNew}>{t('animatorPromo.new')}</span>
                <button
                    type="button"
                    className={styles.close}
                    onClick={onDismiss}
                    aria-label={t('animatorPromo.close')}
                >
                    <IconClose />
                </button>
            </div>

            <h2 className={styles.cardTitle}>{t('animatorPromo.title')}</h2>
            <p className={styles.cardText}>{t('animatorPromo.text')}</p>

            <a
                className={styles.cardQrRow}
                href={link}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => logEvent('animator_promo_modal_clicked', clientID, { clientID })}
            >
                <span className={styles.qrFrame}>
                    <QRCodeSVG value={link} size={68} bgColor="#ffffff" fgColor="#070219" level="M" />
                </span>
                <span className={styles.cardQrLabel}>
                    {t('animatorPromo.scan1')}
                    <br />
                    {t('animatorPromo.scan2')}
                    <svg
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden="true"
                    >
                        <path d="M5 12h14M13 6l6 6-6 6" />
                    </svg>
                </span>
            </a>
        </aside>
    );
}

function AnimatorPromoBadge({ clientID, link, inColumn }: { clientID: string; link: string; inColumn?: boolean }) {
    const { t } = useTranslation();
    return (
        <a
            className={`${styles.badge} ${inColumn ? styles.inColumn : ''}`}
            href={link}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={t('animatorPromo.badgeAria')}
            onClick={() => logEvent('animator_promo_encart_clicked', clientID, { clientID })}
        >
            <span className={styles.badgeQr}>
                <QRCodeSVG value={link} size={34} bgColor="#ffffff" fgColor="#070219" level="M" />
            </span>
            <span className={styles.badgeLabel}>
                <span className={styles.badgeIcon}>
                    <IconPhone />
                </span>
                {t('animatorPromo.badge')}
            </span>
        </a>
    );
}

export function AnimatorPromo({ clientID, inColumn }: AnimatorPromoProps) {
    // useLocalStorage handles the Safari-private try/catch internally. The mount
    // is keyed by clientID in HomeAnimation, so the right key is read on switch.
    const [dismissed, setDismissed] = useLocalStorage(dismissKey(clientID), false);

    const link = `${window.location.origin}/animator/${clientID}`;

    const dismiss = () => {
        logEvent('animator_promo_modal_dismissed', clientID, { clientID });
        setDismissed(true);
    };

    if (dismissed) {
        return <AnimatorPromoBadge clientID={clientID} link={link} inColumn={inColumn} />;
    }
    return <AnimatorPromoSpotlight clientID={clientID} link={link} onDismiss={dismiss} inColumn={inColumn} />;
}
